// ============================================================================
// Line Official Account — outbound message helpers.
//
// Two mutually-exclusive endpoints:
//   - `/v2/bot/message/reply`  — FREE. Requires a fresh `replyToken` (30s,
//                                  one-shot). Preferred when available.
//   - `/v2/bot/message/push`   — CHARGED. Sends to a user id directly.
//
// Both require `Authorization: Bearer <channel_access_token>`. Never put the
// token in the URL — Cloudflare edge + proxy logs capture full URLs.
//
// Canonical docs:
//   - https://developers.line.biz/en/reference/messaging-api/#send-reply-message
//   - https://developers.line.biz/en/reference/messaging-api/#send-push-message
// ============================================================================

import type { MessageAttachment } from '../../capabilities/messaging'
import {
  classifyLineError,
  LineOaError,
  parseRetryAfterMs,
  type LineErrorEnvelope,
  type LineSendResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

/** Default Messaging API base. Injectable for tests via fetchImpl URL patching. */
export const LINE_API_BASE = 'https://api.line.me'
const REPLY_ENDPOINT = `${LINE_API_BASE}/v2/bot/message/reply`
const PUSH_ENDPOINT = `${LINE_API_BASE}/v2/bot/message/push`

// Line caps a single reply/push at 5 message objects per request. Callers
// exceeding this hit a 400; we trim in the builder to keep errors actionable.
const LINE_MAX_MESSAGES_PER_SEND = 5

// Issue #249 M5: Line recipient ids are documented as:
//   User id:  "U" + 32 hex chars   (^U[0-9a-fA-F]{32}$)
//   Room id:  "R" + 32 hex chars   (^R[0-9a-fA-F]{32}$)
//   Group id: "C" + 32 hex chars   (^C[0-9a-fA-F]{32}$)
// Catching typos here (e.g. the adapter caller passing a Facebook PSID, or a
// userId that's been URL-encoded / trimmed) is much nicer than waiting for
// Line to reject with an opaque 400. Push is the hot path; we gate on it.
const LINE_RECIPIENT_RE = /^[URC][0-9a-fA-F]{32}$/

// ============================================================================
// Public types
// ============================================================================

/** A Line message object — text / image / video / audio / location / sticker /
 *  flex / template / imagemap. We model only the types the adapter builds
 *  directly; callers that want raw flex / template pass `LineRawMessage`. */
export type LineMessage =
  | LineTextMessage
  | LineImageMessage
  | LineVideoMessage
  | LineAudioMessage
  | LineLocationMessage
  | LineStickerMessage
  | LineFlexMessage
  | LineRawMessage

export interface LineTextMessage {
  type: 'text'
  text: string
  quickReply?: LineQuickReply
}

export interface LineImageMessage {
  type: 'image'
  originalContentUrl: string
  previewImageUrl: string
  quickReply?: LineQuickReply
}

export interface LineVideoMessage {
  type: 'video'
  originalContentUrl: string
  previewImageUrl: string
  quickReply?: LineQuickReply
}

export interface LineAudioMessage {
  type: 'audio'
  originalContentUrl: string
  duration: number
  quickReply?: LineQuickReply
}

export interface LineLocationMessage {
  type: 'location'
  title: string
  address: string
  latitude: number
  longitude: number
}

export interface LineStickerMessage {
  type: 'sticker'
  packageId: string
  stickerId: string
}

export interface LineFlexMessage {
  type: 'flex'
  altText: string
  contents: unknown
  quickReply?: LineQuickReply
}

/** Escape hatch for callers passing a pre-built message object (template,
 *  imagemap, multicast-specific shapes not yet modelled here). */
export interface LineRawMessage {
  type: string
  [key: string]: unknown
}

export interface LineQuickReply {
  items: Array<{
    type: 'action'
    imageUrl?: string
    action: LineQuickReplyAction
  }>
}

export type LineQuickReplyAction =
  | { type: 'message'; label: string; text: string }
  | { type: 'postback'; label: string; data: string; displayText?: string }
  | { type: 'uri'; label: string; uri: string }

// ============================================================================
// sendLineReply — free-tier reply using the 30s replyToken
// ============================================================================

export async function sendLineReply(
  replyToken: string,
  messages: LineMessage[],
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LineSendResponse> {
  assertNonEmpty(replyToken, 'replyToken')
  assertAccessToken(accessToken)
  assertMessages(messages)

  const body = {
    replyToken,
    messages: capMessages(messages),
  }

  const response = await fetchImpl(REPLY_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  // Line returns `{}` on success; tolerate empty body.
  return (await safeReadJson<LineSendResponse>(response)) ?? {}
}

// ============================================================================
// sendLinePush — charged direct push to a userId / groupId / roomId
// ============================================================================

export async function sendLinePush(
  to: string,
  messages: LineMessage[],
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LineSendResponse> {
  assertNonEmpty(to, 'to')
  assertLineRecipient(to)
  assertAccessToken(accessToken)
  assertMessages(messages)

  const body = {
    to,
    messages: capMessages(messages),
  }

  const response = await fetchImpl(PUSH_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  return (await safeReadJson<LineSendResponse>(response)) ?? {}
}

// ============================================================================
// Message builders — keep construction adapter-local so callers stay
// provider-agnostic.
// ============================================================================

/** Build a `text` message from a plain string. Line caps text at 5,000 chars
 *  on reply/push; we trim defensively so callers don't hit 400. */
export function buildLineTextMessage(text: string): LineTextMessage {
  const trimmed = typeof text === 'string' ? text.slice(0, 5000) : ''
  return { type: 'text', text: trimmed }
}

/** Build a `quickReply` object from the capability-level
 *  `{label, payload}[]` shape. Maps each option to a postback action — text
 *  actions would echo back into the conversation which is rarely the intent
 *  for a quick-reply button. */
export function buildLineQuickReply(
  options: Array<{ label: string; payload: string }>,
): LineQuickReply {
  // Line caps quickReply.items at 13; trim defensively.
  const capped = options.slice(0, 13)
  return {
    items: capped.map((o) => ({
      type: 'action',
      action: {
        type: 'postback',
        label: o.label.slice(0, 20), // Line caps label at 20 chars
        data: o.payload.slice(0, 300), // Line caps postback data at 300 chars
        displayText: o.label.slice(0, 300),
      },
    })),
  }
}

/**
 * Map a capability-level `MessageAttachment[]` into Line message objects.
 *
 * Rules:
 *   - `image`    → 1x LineImageMessage (originalContentUrl + previewImageUrl
 *                   both pointing to the same URL; Line requires both).
 *   - `video`    → LineVideoMessage (requires a preview — we reuse the main
 *                   URL; callers wanting a proper preview should build the
 *                   message manually).
 *   - `audio`    → LineAudioMessage with duration=0 (Line requires the
 *                   field; an unknown duration still sends).
 *   - `file`     → NOT a native Line type. We degrade to a text message
 *                   carrying the file URL so the user still sees it.
 *   - `quick_replies` → attached to the LAST prior message's `quickReply`
 *                        field (not a standalone message). If no prior
 *                        message exists, the quick replies are dropped.
 */
export function buildMessagesFromAttachments(
  attachments: MessageAttachment[] | undefined,
): LineMessage[] {
  if (!Array.isArray(attachments) || attachments.length === 0) return []
  const out: LineMessage[] = []
  for (const a of attachments) {
    switch (a.type) {
      case 'image':
        out.push({
          type: 'image',
          originalContentUrl: a.url,
          previewImageUrl: a.url,
        })
        break
      case 'video':
        out.push({
          type: 'video',
          originalContentUrl: a.url,
          previewImageUrl: a.url,
        })
        break
      case 'audio':
        out.push({
          type: 'audio',
          originalContentUrl: a.url,
          duration: 0,
        })
        break
      case 'file':
        // Degrade to a text message carrying a link. Line has no native file
        // message (files only arrive inbound from users; outbound must be a
        // link in a text / imagemap).
        out.push({
          type: 'text',
          text: a.filename ? `${a.filename}: ${a.url}` : a.url,
        })
        break
      case 'quick_replies':
        if (out.length > 0) {
          const last = out[out.length - 1] as LineMessage & { quickReply?: LineQuickReply }
          last.quickReply = buildLineQuickReply(a.options)
        }
        break
      default:
        break
    }
  }
  return out
}

// ============================================================================
// Internal helpers
// ============================================================================

function capMessages(messages: LineMessage[]): LineMessage[] {
  if (messages.length <= LINE_MAX_MESSAGES_PER_SEND) return messages
  // Drop beyond 5 — Line returns 400 otherwise, and there's no way to
  // partial-send (reply/push is all-or-nothing).
  return messages.slice(0, LINE_MAX_MESSAGES_PER_SEND)
}

async function errorFromResponse(response: Response): Promise<LineOaError> {
  let envelope: LineErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as LineErrorEnvelope
  } catch {
    // Non-JSON error body — leave envelope null.
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  return classifyLineError(envelope, response.status, retryAfterMs)
}

async function safeReadJson<T>(response: Response): Promise<T | null> {
  try {
    const text = await response.text()
    if (text.length === 0) return null
    return JSON.parse(text) as T
  } catch {
    return null
  }
}

function assertNonEmpty(value: string | undefined | null, field: string): void {
  if (typeof value !== 'string' || value.length === 0) {
    throw new LineOaError('INVALID_INPUT', `${field} is required`)
  }
}

/**
 * Reject a Line recipient id that doesn't match the documented shape:
 *
 *   "U" + 32 hex  (user)
 *   "R" + 32 hex  (room)
 *   "C" + 32 hex  (group)
 *
 * We surface as `INVALID_INPUT` BEFORE the push so typos fail fast with an
 * actionable error instead of bouncing off Line with a generic 400. Exported
 * so the capability layer can apply the same guard on push-adjacent flows
 * (`sendMessage` entry point). (Issue #249 M5.)
 */
export function assertLineRecipient(to: string): void {
  if (typeof to !== 'string' || !LINE_RECIPIENT_RE.test(to)) {
    throw new LineOaError(
      'INVALID_INPUT',
      `Line recipient id must match ^[URC][0-9a-fA-F]{32}$ (got "${to}")`,
    )
  }
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new LineOaError('INVALID_INPUT', 'accessToken is required')
  }
}

function assertMessages(messages: LineMessage[] | undefined | null): void {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new LineOaError('INVALID_INPUT', 'messages[] is required')
  }
}
